import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR } from "@/components/AppTopBar";

export function WalletMovementPanel({ onDone }) {
  const [stores, setStores] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const shown = rows.filter((a) => { const s = q.trim().toLowerCase(); if (!s) return true; return [a.linked_order_id, a.adjustment_type, a.reason, a.note, a.movement_id].some((v) => String(v || "").toLowerCase().includes(s)); });
  const [form, setForm] = useState({ store_id: "", account_id: "", direction: "OUT", amount: "", date: "", category: "", reference: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadRows = () => api.get("/marketplace/adjustments").then(({ data }) => setRows(data.filter((a) => (a.adjustment_type || "").startsWith("MANUAL_WALLET_")))).catch(() => {});
  useEffect(() => {
    api.get("/mappings/stores").then(({ data }) => {
      const seen = new Map();
      (Array.isArray(data) ? data : []).forEach((r) => { if (r.store_id && !seen.has(r.store_id)) seen.set(r.store_id, r.store_name || r.store_id); });
      setStores(Array.from(seen, ([id, name]) => ({ id, name })));
    }).catch(() => {});
    api.get("/fund/wallets").then(({ data }) => setWallets((data || []).filter((w) => !w.account_type || w.account_type === "MARKETPLACE_WALLET"))).catch(() => {});
    loadRows();
  }, []);

  const submit = async () => {
    setErr(""); setMsg("");
    if (!form.store_id || !form.account_id || !form.category.trim() || !form.amount) { setErr("Store, Wallet, Nominal, dan Kategori wajib diisi."); return; }
    setBusy(true);
    try {
      const body = { ...form, amount: parseFloat(form.amount), idempotency_key: crypto.randomUUID() };
      const { data } = await api.post("/marketplace/wallet-movement", body);
      setMsg(`Tersimpan (${data.direction}, tier ${data.tier}). Ref movement: ${data.movement_id}`);
      setForm({ ...form, amount: "", category: "", reference: "", note: "" });
      loadRows(); if (onDone) onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "DUPLICATE_WALLET_MUTATION") {
        setErr(`DUPLIKAT — ${d.message} Existing: id ${d.existing_id} · movement ${d.existing_movement_id} · ${d.created_at || "-"}`);
      } else {
        setErr(typeof d === "object" ? (d.message || JSON.stringify(d)) : (formatApiErrorDetail(d) || "Gagal menyimpan wallet movement."));
      }
    } finally { setBusy(false); }
  };

  const reverse = async (id) => {
    const reason = window.prompt("Alasan reversal?");
    if (!reason) return;
    setErr(""); setMsg("");
    try {
      await api.post(`/marketplace/wallet-movement/${id}/reverse`, { reason });
      setMsg("Reversal berhasil.");
      loadRows(); if (onDone) onDone();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Reversal gagal."); }
  };

  const fieldWrap = { display: "flex", flexDirection: "column", gap: 4 };
  const ellip = { maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  return (
    <>
      <section className="panel" data-testid="wallet-movement-panel">
        <h2 className="panel-title">Mutasi Wallet Manual (IN / OUT)</h2>
        <p className="hint" style={{ marginTop: -6 }}>Untuk kejadian wallet nyata (refund debit, penalty, kompensasi, klaim). Read-only terhadap Settlement; OUT memakai hierarki traceable → proporsional aman.</p>
        {err && <div className="alert-error" data-testid="wallet-mv-error">{err}</div>}
        {msg && <div className="alert-success" data-testid="wallet-mv-success">{msg}</div>}

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
          <div style={{ ...fieldWrap, flex: "2 1 200px", minWidth: 180 }}><label className="field-label">Store</label>
            <select className="field-input" value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} data-testid="wallet-mv-store">
              <option value="">— pilih —</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <div style={{ ...fieldWrap, flex: "2 1 200px", minWidth: 180 }}><label className="field-label">Wallet</label>
            <select className="field-input" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} data-testid="wallet-mv-account">
              <option value="">— pilih —</option>{wallets.map((w) => <option key={w.account_id} value={w.account_id}>{w.account_name}</option>)}</select></div>
          <div style={{ ...fieldWrap, flex: "0 0 132px" }}><label className="field-label">Arah</label>
            <select className="field-input" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="wallet-mv-direction">
              <option value="OUT">OUT (debit)</option><option value="IN">IN (kredit)</option></select></div>
          <div style={{ ...fieldWrap, flex: "0 0 140px" }}><label className="field-label">Nominal</label>
            <input className="field-input" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="wallet-mv-amount" /></div>
          <div style={{ ...fieldWrap, flex: "0 0 160px" }}><label className="field-label">Tanggal</label>
            <input className="field-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="wallet-mv-date" /></div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ ...fieldWrap, flex: "1.2 1 190px", minWidth: 180 }}><label className="field-label">Kategori</label>
            <input className="field-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="REFUND_DEBIT / PENALTY / OPENING_RECONCILIATION" list="wallet-mv-category-options" data-testid="wallet-mv-category" />
            <datalist id="wallet-mv-category-options" data-testid="wallet-mv-category-options">
              <option value="REFUND_DEBIT" />
              <option value="PENALTY" />
              <option value="COMPENSATION" />
              <option value="CLAIM" />
              <option value="OPENING_RECONCILIATION" />
            </datalist></div>
          <div style={{ ...fieldWrap, flex: "1.5 1 210px", minWidth: 200 }}><label className="field-label">Referensi Marketplace</label>
            <input className="field-input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Order ID / Claim ID (opsional)" data-testid="wallet-mv-reference" /></div>
          <div style={{ ...fieldWrap, flex: "2 1 220px", minWidth: 200 }}><label className="field-label">Catatan</label>
            <input className="field-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="wallet-mv-note" /></div>
          <div style={{ flex: "0 0 auto" }}>
            <button className="btn-primary" onClick={submit} disabled={busy} data-testid="wallet-mv-submit">{busy ? "Menyimpan…" : "Catat Mutasi"}</button>
          </div>
        </div>
      </section>

      <section className="panel" data-testid="wallet-movement-history-panel">
        <h2 className="panel-title">Riwayat Mutasi Wallet Manual</h2>
        <input className="field-input" data-testid="wallet-history-search" placeholder="Cari nomor resi / referensi..." value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 8, maxWidth: 420 }} />
        <div className="table-scroll">
          <table className="data-table" data-testid="wallet-mv-table">
            <thead><tr>
              <th>Tanggal</th><th>Store</th><th>Arah</th>
              <th>Kategori</th><th>Referensi</th><th>Nominal</th>
              <th>Status</th><th>Aksi</th>
            </tr></thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={8} className="empty-cell" data-testid="wallet-mv-empty">{rows.length === 0 ? "Belum ada mutasi wallet manual." : "Tidak ada mutasi yang cocok dengan pencarian."}</td></tr>}
              {shown.map((a) => {
                const kategori = `${(a.adjustment_type || "").replace("MANUAL_WALLET_", "")}${a.reason ? ` · ${a.reason}` : ""}`;
                return (
                  <tr key={a.id} data-testid={`wallet-mv-row-${a.id}`}>
                    <td className="mono">{a.release_date || a.adjustment_date || "-"}</td>
                    <td><div style={ellip} title={a.store_name || "-"}>{a.store_name || "-"}</div></td>
                    <td><span className={`status-pill ${a.direction === "IN" ? "on" : "dir-out"}`}>{a.direction}</span></td>
                    <td><div style={ellip} title={kategori}>{kategori || "-"}</div></td>
                    <td className="mono"><div style={ellip} title={a.linked_order_id || "-"}>{a.linked_order_id || "-"}</div></td>
                    <td className="mono">{formatIDR(a.amount)}</td>
                    <td><span className={`status-pill ${a.status === "COMMITTED" ? "on" : "off"}`}>{a.status}</span></td>
                    <td style={{ whiteSpace: "nowrap" }}>{a.status === "COMMITTED" && a.adjustment_type !== "MANUAL_WALLET_REVERSAL" && <button className="btn-mini" onClick={() => reverse(a.id)} data-testid={`wallet-mv-reverse-${a.id}`}>Batalkan</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
